// Libs
import classNames from 'classnames/bind';
import { Box, Button, Stack } from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import { BaseInputSearch, BaseMoreAction, BaseTable, BaseToolbar, ConfirmModal, Loading } from '~/components/common';
import { CategoryFormModal } from '~/components/module/setting';
// Others
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  EMPTY_STRING,
} from '~/utils/constants/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { IPaginationResponse } from '~/utils/interface/base';
import { deleteCategory, getListCategory } from '~/thunks/category/categoryThunk';
import { categoryActions } from '~/thunks/category/categorySlice';
import { ICategory } from '~/utils/interface/category';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { IBaseMoreAction, IHeaderCellType, IQueryParamBase, ISortBy } from '~/utils/interface/common';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { CATEGORY_PERMISSIONS } from '~/utils/constants/permissions';
// Styles, images, icons
import styles from './Category.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsCategory = (
  t: TFunction,
  sortBy: ISortBy[],
  onEdit: (id: string) => void,
  onDelete: (id: string) => void,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<ICategory>[] => {
  return [
    {
      id: 'name',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_name_title')}</Box>
        </Stack>
      ),
      render: (record: ICategory) => <Box className={cx('textValue')}>{record?.name}</Box>,
    },
    {
      id: 'description',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_description_title')}</Box>
        </Stack>
      ),
      render: (record: ICategory) => <Box className={cx('textValue')}>{record?.description}</Box>,
    },
    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: ICategory) => {
        const actions: IBaseMoreAction[] = [
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onDelete(record.id),
                  hasOtherColor: true,
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Category = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreateCategory, canViewCategory, canEditCategory, canDeleteCategory } =
    usePermissions(CATEGORY_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/category', 'common/base']);
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  const { isLoading, isRefreshCategoryList } = useAppSelector((state) => state.category);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>([]);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);
  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [isOpenCategoryFormModal, setIsOpenCategoryFormModal] = useState<boolean>(false);
  const [idCategory, setIdCategory] = useState<string>(EMPTY_STRING);
  const [categoryList, setCategoryList] = useState<ICategory[]>([]);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [totalItems, setTotalItems] = useState<number>(0);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListCategory(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshCategoryList) return;

    handleGetListCategory(paramObject);

    dispatch(categoryActions.setRefreshCategoryList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshCategoryList]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListCategory = (params: IQueryParamBase) => {
    dispatch(getListCategory(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setCategoryList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {});
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleShowCategoryFormModal = () => {
    setIsOpenCategoryFormModal((prev) => !prev);
    setIdCategory(EMPTY_STRING);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleCloseConfirmDelete = () => {
    setIdCategory(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleEdit = (id: string) => {
    if (!id) return;

    setIdCategory(id);
    setIsOpenCategoryFormModal(true);
  };

  const handleDelete = (id: string) => {
    if (!id) return;

    setIdCategory(id);
    setIsShowConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteCategory(idCategory))
      .unwrap()
      .then((res) => {})
      .catch((_error) => {})
      .finally(() => {
        handleCloseConfirmDelete();
      });
  };
  //#endregion Handle Function

  return (
    <Box id='categoryPage' className={cx('container')}>
      <BaseToolbar title={t('category_title')} isBack description={t('category_sub_title')}>
        <BaseInputSearch
          width={300}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('common_text_search', { ns: 'common/base' })}
        />

        {canCreateCategory && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleShowCategoryFormModal}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('category_title_button_add')}
            </Button>
          </Box>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewCategory ? categoryList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsCategory(t, sortBy, handleEdit, handleDelete, canEditCategory, canDeleteCategory)}
          onPageChange={handlePageChange}
          {...(canEditCategory ? { onRowClick: handleEdit } : {})}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isOpenCategoryFormModal && <CategoryFormModal idCategory={idCategory} onClose={handleShowCategoryFormModal} />}

      {isLoading && <Loading />}
    </Box>
  );
};

export default Category;
